/**
 * Re-home an override's resource references into the scope of the node it overrides.
 * Godot resolves each property in the file that wrote it (`resource_format_text.cpp:125-151`),
 * but a component resolves all of a node's properties in one scope. So each resource the
 * override names joins the node's scope, under a fresh id where the node's file holds that id.
 */
import { renameResourceRefs } from '../godot/variant/resourceRef.js';
import { findExtResource, findSubResource } from './SubResourceResolver.js';
/** Appended to an id the target scope already holds, until the id is free. */
const COLLISION_SUFFIX = ' (outer)';
/**
 * `raw`, authored against `from`, rewritten to resolve against the returned scope, which is
 * `into` plus the resources `raw` reaches in `from`, its SubResources' own references included.
 * An id `from` lacks resolves to nothing. Godot refuses such a file (`resource_format_text.cpp:112`, `:135-137`).
 */
export function rehomeOverride(raw, from, into) {
    const external = new IdPool(into.externalResources);
    const internal = new IdPool(into.internalResources);
    const addedExternal = [];
    const addedInternal = [];
    const rehomeRef = (ref) => {
        const pool = ref.kind === 'ExtResource' ? external : internal;
        const known = pool.renamed.get(ref.id);
        if (known !== undefined)
            return known;
        const id = pool.claim(ref.id);
        if (ref.kind === 'ExtResource') {
            const resource = findExtResource(from.externalResources, ref.id);
            if (resource)
                addedExternal.push({ ...resource, id, authoredId: authoredIdOf(resource) });
        }
        else {
            const resource = findSubResource(from.internalResources, ref.id);
            // Claimed before its data is re-homed, so a reference back to it reuses the claimed id.
            if (resource) {
                addedInternal.push({
                    ...resource,
                    id,
                    authoredId: authoredIdOf(resource),
                    data: mapValues(resource.data, rehomeValue),
                });
            }
        }
        return id;
    };
    const rehomeValue = (value) => renameResourceRefs(value, rehomeRef);
    const rehomedRaw = mapValues(raw, rehomeValue);
    if (external.renamed.size === 0 && internal.renamed.size === 0)
        return { raw, scope: into };
    // `into` itself when nothing joins it: a caller compares scopes by identity.
    if (addedExternal.length === 0 && addedInternal.length === 0)
        return { raw: rehomedRaw, scope: into };
    return {
        raw: rehomedRaw,
        scope: {
            ...into,
            externalResources: [...into.externalResources, ...addedExternal],
            internalResources: [...into.internalResources, ...addedInternal],
        },
    };
}
/** The id the first file wrote: a resource re-homed again keeps it through every nesting level. */
function authoredIdOf(resource) {
    return resource.authoredId ?? resource.id;
}
/** The ids one kind of resource holds in the target scope, and the ones the override claimed. */
class IdPool {
    resources;
    /** Built on the first claim: most overrides name no resource. */
    taken;
    /** Each id the override named, mapped to the id it holds in the target scope. */
    renamed = new Map();
    constructor(resources) {
        this.resources = resources;
    }
    /** A free id for `id`: `id` itself unless the target scope holds it. */
    claim(id) {
        this.taken ??= new Set(this.resources.map((r) => r.id));
        let free = id;
        while (this.taken.has(free))
            free += COLLISION_SUFFIX;
        this.taken.add(free);
        this.renamed.set(id, free);
        return free;
    }
}
function mapValues(record, map) {
    return Object.fromEntries(Object.entries(record).map(([key, value]) => [key, map(value)]));
}
//# sourceMappingURL=rehomeOverride.js.map